import { Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { BfxBlotterFilter } from "app/bfx-blotter-filter/bfx-blotter-filter.constants";
import { BmarkClientOrdersDataService } from "app/bfx-bmark-summary/blotters/client-orders/services/bmark-client-orders-data.service";
import { BmarkClientOrdersFilterService } from "app/bfx-bmark-summary/blotters/client-orders/services/bmark-client-orders-filter.service";
import { BmarkFixingsManagerService } from "app/bfx-bmark-summary/services/bmark-fixings-manager.service";
import { BmarkSummaryUtilService } from "app/bfx-bmark-summary/services/bmark-summary-util.service";
import { BigNumber } from "shared/cubiq-model-multi-asset";
import {
  BenchmarkDataPrimaryLookupService,
  BmarkFixingType,
  CcyLookupService,
  HedgeOrderDataPrimaryLookupService,
  TraderGroupLookupService,
} from "shared/cubiq-services";
import {
  BmarkRiskOrderDataProvider,
  RiskBMOrderDataListener,
  RiskSubscriptionType,
} from "shared/cubiq-services/lookup/model/impl/bmrisk/BaseBMRiskModel";
import { SpotRiskModel } from "shared/cubiq-services/lookup/model/impl/bmrisk/SpotRiskModel";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import { BmarkClientOrdersBlotterMode } from "shared/cubiq-services/model/benchmarks/enums/BmarkClientOrdersBlotterMode.enum";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

class SimpleSpotRiskBmarkOrderDataProvider implements BmarkRiskOrderDataProvider {
  constructor(public orderData: Set<BmarkOrderData>) {}

  primeData(): Promise<Set<BmarkOrderData>> {
    return new Promise<Set<BmarkOrderData>>((res, rej) => {
      res(this.orderData);
    });
  }

  subscribe(type: RiskSubscriptionType, listener: RiskBMOrderDataListener) {
    // no op
  }
}

@Injectable()
export class BfxHedgeOrderMonitorSpotRiskService {
  protected spotRiskData: any = {};

  constructor(
    protected clientOrderService: BenchmarkDataPrimaryLookupService,
    protected hedgeOrderService: HedgeOrderDataPrimaryLookupService,
    protected clientDataService: BmarkClientOrdersDataService,
    protected fixingService: BmarkFixingsManagerService,
    protected traderGroupService: TraderGroupLookupService,
    protected currencyLookupService: CcyLookupService,
    protected filterService: BmarkClientOrdersFilterService,
    protected utilService: BmarkSummaryUtilService,
    protected cmdLine: ActivatedRoute,
  ) {}

  public getPositionTotals(
    fixingId: string,
    currencyPairs: string[],
    dealt: boolean,
  ): Promise<{ pair: string; total: BigNumber }[]> {
    let clientOrderPromise: Promise<BmarkOrderData[]> =
      this.clientOrderService.getBmarkOrderDataForFixingId(fixingId, BmarkFixingType.ACTIVE);
    let hedgeOrderPromise: Promise<BmarkOrderData[]> =
      this.hedgeOrderService.getBmarkOrderDataForFixingId(fixingId, BmarkFixingType.ACTIVE);
    let filterPromise: Promise<BfxBlotterFilter[]> = this.clientDataService.getAppliedFilters();

    return new Promise<{ pair: string; total: BigNumber }[]>((resolve) => {
      Promise.all([clientOrderPromise, hedgeOrderPromise, filterPromise]).then(
        ([clientOrders, hedgeOrders, filters]) => {
          clientOrders = this.clientDataService.processOrderData(
            clientOrders,
            dealt ? BmarkClientOrdersBlotterMode.DEALT : BmarkClientOrdersBlotterMode.RISK,
          );

          try {
            if (filters != null && filters.length > 0) {
              clientOrders = clientOrders.filter((row) =>
                this.filterService.doFiltersPass(row, filters),
              );
              hedgeOrders = hedgeOrders.filter((row) =>
                this.filterService.doFiltersPass(row, filters),
              );
            }

            clientOrders = clientOrders.filter((row) =>
              this.utilService.doesQuickFilterPass(row, RiskSubscriptionType.CLIENT),
            );
            hedgeOrders = hedgeOrders.filter((row) =>
              this.utilService.doesQuickFilterPass(row, RiskSubscriptionType.HEDGE),
            );
          } catch (e) {
            logger.out.error(
              `BfxHedgeOrderMonitorSpotRiskService:` + ` error filtering out orders ${e}`,
            );
          }

          let total: any = {};

          currencyPairs.forEach((pair: string) => {
            total[pair] = new BigNumber(0);
          });

          try {
            let clientProvider = new SimpleSpotRiskBmarkOrderDataProvider(new Set());
            let spotRiskModel = new SpotRiskModel(
              clientProvider,
              this.traderGroupService,
              this.currencyLookupService,
              Promise.resolve(true),
              this.cmdLine,
            );

            spotRiskModel.resetData();

            spotRiskModel.onUpdateOrders(new Set(clientOrders), RiskSubscriptionType.CLIENT);
            spotRiskModel.onUpdateOrders(new Set(hedgeOrders), RiskSubscriptionType.HEDGE);

            currencyPairs.forEach((p) => {
              let spotRiskData = spotRiskModel.spotRiskData.get(p);
              if (spotRiskData != null) {
                total[p] = spotRiskData.total;
              }
            });
          } catch (e) {
            logger.out.error(
              `BfxHedgeOrderMonitorSpotRiskService:` + ` error calculating risk total ${e}`,
            );
          }

          resolve(
            currencyPairs.map((pair: string) => {
              return {
                pair: pair,
                total: total[pair],
              };
            }),
          );
        },
      );
    });
  }
}
